import { PGlite, MemoryFS, IdbFs } from "@electric-sql/pglite";
import { electricSync } from "@electric-sql/pglite-sync";
import { live } from "@electric-sql/pglite/live";
import pTimeout, { TimeoutError } from "p-timeout";
import { PGliteWithExtensions } from "@/domains/database/utils/db";
import { DatabaseFilesystemKind } from "@/domains/database/types/db";
import { DatabaseInitializationError } from "@/domains/errors/kinds/database";
import { logger } from "@/modules/logger";

export interface CreateConnectionOptions {
  databaseName: string;
  filesystemKind: DatabaseFilesystemKind;
  timeoutMs: number;
  attemptId: string;
  isAttemptActive: (attemptId: string) => boolean;
}

export async function create({
  databaseName,
  filesystemKind,
  timeoutMs,
  attemptId,
  isAttemptActive,
}: CreateConnectionOptions): Promise<PGliteWithExtensions> {
  const fs =
    filesystemKind === DatabaseFilesystemKind.IndexedDB
      ? new IdbFs(databaseName)
      : new MemoryFS(databaseName);

  const createPromise = PGlite.create({
    fs,
    extensions: {
      electric: electricSync(),
      live,
    },
    relaxedDurability: true,
  });

  // If this attempt resolves after becoming stale, close the handle to prevent leaks.
  createPromise
    .then(async db => {
      if (!isAttemptActive(attemptId)) {
        try {
          await (db as PGliteWithExtensions).close();
          logger.warn({
            message: "[PGliteConnectionHandler] Closed stale PGlite handle",
            info: { databaseName, attemptId },
          });
        } catch (_closeError) {
          // no-op
        }
      }
    })
    .catch(_err => {
      // no-op
    });

  try {
    const db = await pTimeout(createPromise, {
      milliseconds: timeoutMs,
      message: "Create phase timed out",
    });
    return db as PGliteWithExtensions;
  } catch (error) {
    if (error instanceof TimeoutError) {
      throw new DatabaseInitializationError("Create phase timed out", {
        phase: "creation",
        databaseName,
      });
    }
    throw error;
  }
}

export async function close(db: PGliteWithExtensions): Promise<void> {
  await db.close();
}
